import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { LogoIcon } from '@components/ui';
import './LandingPage.css';

// ── Constants ────────────────────────────────────────────────
const PRICE_PER_SEAT = 99;
const CURRENCY = 'lei';
const ANNUAL_DISCOUNT = 17;

// ── Feature item keys (icons are language-independent) ───────
interface FeatureItem {
  key: string;
  icon: ReactElement;
}

const FEATURE_ITEMS: FeatureItem[] = [
  {
    key: 'smartScheduling',
    icon: (
      <svg viewBox="0 0 18 18" fill="none" stroke="#1E5C47" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="14" height="13" rx="2" /><line x1="2" y1="7" x2="16" y2="7" /><line x1="6" y1="1" x2="6" y2="5" /><line x1="12" y1="1" x2="12" y2="5" />
      </svg>
    ),
  },
  {
    key: 'patientRecords',
    icon: (
      <svg viewBox="0 0 18 18" fill="none" stroke="#1E5C47" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="6" r="3" /><path d="M2 16c0-3.314 3.134-6 7-6s7 2.686 7 6" />
      </svg>
    ),
  },
  {
    key: 'autoInvoicing',
    icon: (
      <svg viewBox="0 0 18 18" fill="none" stroke="#1E5C47" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H4a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V4a2 2 0 00-2-2z" /><line x1="6" y1="6" x2="12" y2="6" /><line x1="6" y1="9" x2="12" y2="9" /><line x1="6" y1="12" x2="9" y2="12" />
      </svg>
    ),
  },
  {
    key: 'integratedPayments',
    icon: (
      <svg viewBox="0 0 18 18" fill="none" stroke="#1E5C47" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="16" height="11" rx="2" /><path d="M1 8h16" />
      </svg>
    ),
  },
  {
    key: 'reportsStats',
    icon: (
      <svg viewBox="0 0 18 18" fill="none" stroke="#1E5C47" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12l4-4 3 3 4-5 3 3" /><rect x="1" y="1" width="16" height="16" rx="2" />
      </svg>
    ),
  },
  {
    key: 'autoReminders',
    icon: (
      <svg viewBox="0 0 18 18" fill="none" stroke="#1E5C47" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 10a6 6 0 11-12 0 6 6 0 0112 0z" /><path d="M9 7v3l2 2" />
      </svg>
    ),
  },
];

const PRICING_FEATURE_KEYS = [
  'unlimitedAppointments',
  'patientRecords',
  'autoNotifications',
  'invoicingPayments',
  'reportsStats',
  'calendarIntegration',
  'importExport',
  'supportIncluded',
] as const;

const FAQ_KEYS = [
  'setupTime',
  'importData',
  'dataSafety',
  'mobileSupport',
  'afterTrial',
] as const;

const STEPS = [
  { key: 'createAccount', num: '01.' },
  { key: 'importData', num: '02.' },
  { key: 'startWorking', num: '03.' },
] as const;

const TESTIMONIALS = [
  { key: 'sarah', av: 'DO', bg: '#2D7A5E' },
  { key: 'marcus', av: 'ML', bg: '#1E5C47' },
  { key: 'ana', av: 'AC', bg: '#B07D2E' },
] as const;

// ── Sections ─────────────────────────────────────────────────
function HamburgerIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="20" height="20" aria-hidden="true">
      <path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="20" height="20" aria-hidden="true">
      <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function LandingNav() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const close = () => setMenuOpen(false);

  return (
    <nav>
      <div className="nav-inner">
        <a href="/" className="nav-logo">
          <div className="nav-logo-icon"><LogoIcon size={18} /></div>
          <span className="nav-logo-name">calendo</span>
        </a>
        <div className="nav-links">
          <a href="#features" className="nav-link">{t('nav.features')}</a>
          <a href="#how" className="nav-link">{t('nav.howItWorks')}</a>
          <a href="#pricing" className="nav-link">{t('nav.pricing')}</a>
          <a href="#faq" className="nav-link">{t('nav.faq')}</a>
        </div>
        <div className="nav-actions">
          <a href="/login" className="nav-auth">{t('nav.signIn')}</a>
          <a href="#pricing" className="btn-primary">{t('nav.freeTrial')}</a>
        </div>
        <button
          className="nav-mobile-toggle"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={t('layout.toggleMenu')}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <CloseIcon /> : <HamburgerIcon />}
        </button>
      </div>
      {menuOpen && (
        <div className="nav-mobile-menu">
          <a href="#features" className="nav-mobile-link" onClick={close}>{t('nav.features')}</a>
          <a href="#how" className="nav-mobile-link" onClick={close}>{t('nav.howItWorks')}</a>
          <a href="#pricing" className="nav-mobile-link" onClick={close}>{t('nav.pricing')}</a>
          <a href="#faq" className="nav-mobile-link" onClick={close}>{t('nav.faq')}</a>
          <div className="nav-mobile-actions">
            <a href="/login" className="nav-mobile-auth" onClick={close}>{t('nav.signIn')}</a>
            <a href="/register" className="btn-primary nav-mobile-cta" onClick={close}>{t('nav.freeTrial')}</a>
          </div>
        </div>
      )}
    </nav>
  );
}

function HeroSection() {
  const { t } = useTranslation();
  return (
    <section className="hero">
      <div className="hero-inner">
        <div>
          <div className="hero-eyebrow">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <circle cx="6" cy="6" r="4" fill="#1E5C47" />
            </svg>
            {t('hero.eyebrow')}
          </div>
          <h1 className="hero-title">
            {t('hero.titleLine1')}<br />
            <em>{t('hero.titleEm')}</em>
          </h1>
          <p className="hero-body">{t('hero.body')}</p>
          <div className="hero-actions">
            <a href="#pricing" className="btn-primary btn-lg">{t('hero.ctaPrimary')}</a>
            <a href="#how" className="btn-secondary btn-lg">{t('hero.ctaSecondary')}</a>
          </div>
          <p className="hero-note">{t('hero.note')}</p>
        </div>

        <div className="hero-mockup">
          <div className="mockup-topbar">
            <div className="mockup-dot" style={{ background: '#E4E0D8' }} />
            <div className="mockup-dot" style={{ background: '#E4E0D8' }} />
            <div className="mockup-dot" style={{ background: '#E4E0D8' }} />
            <span className="mockup-title">{t('hero.mockup.title')}</span>
          </div>
          <div className="mockup-body">
            <div className="mockup-stat-row">
              <div className="mockup-stat">
                <div className="mockup-stat-num">247</div>
                <div className="mockup-stat-label">{t('hero.mockup.activePatients')}</div>
              </div>
              <div className="mockup-stat">
                <div className="mockup-stat-num">38</div>
                <div className="mockup-stat-label">{t('hero.mockup.appointmentsToday')}</div>
              </div>
              <div className="mockup-stat">
                <div className="mockup-stat-num">94%</div>
                <div className="mockup-stat-label">{t('hero.mockup.attendanceRate')}</div>
              </div>
            </div>
            <div className="mockup-appt">
              <div className="mockup-appt-header">{t('hero.mockup.todaysAppointments')}</div>
              <div className="mockup-appt-row">
                <div className="mockup-av" style={{ background: '#2D7A5E' }}>MI</div>
                <span className="mockup-name">Maria Ionescu</span>
                <span className="mockup-time">09:00</span>
                <span className="mockup-badge" style={{ background: '#E8F0ED', color: '#1E5C47' }}>{t('hero.mockup.confirmed')}</span>
              </div>
              <div className="mockup-appt-row">
                <div className="mockup-av" style={{ background: '#1E5C47' }}>AP</div>
                <span className="mockup-name">Andrei Popa</span>
                <span className="mockup-time">09:45</span>
                <span className="mockup-badge" style={{ background: '#E8F0ED', color: '#1E5C47' }}>{t('hero.mockup.confirmed')}</span>
              </div>
              <div className="mockup-appt-row">
                <div className="mockup-av" style={{ background: '#B07D2E' }}>EC</div>
                <span className="mockup-name">Elena Constantin</span>
                <span className="mockup-time">10:30</span>
                <span className="mockup-badge" style={{ background: '#FDF3E0', color: '#B07D2E' }}>{t('hero.mockup.pending')}</span>
              </div>
              <div className="mockup-appt-row">
                <div className="mockup-av" style={{ background: '#2D7A5E' }}>BD</div>
                <span className="mockup-name">Bogdan Dumitrescu</span>
                <span className="mockup-time">11:15</span>
                <span className="mockup-badge" style={{ background: '#E8F0ED', color: '#1E5C47' }}>{t('hero.mockup.confirmed')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LogosSection() {
  const { t } = useTranslation();
  return (
    <section className="logos">
      <div className="container">
        <div className="logos-label">{t('logos.label')}</div>
        <div className="logos-row">
          <span className="logo-item">Clinica Sf. Maria București</span>
          <span className="logo-item">Cabinet Dr. Ionescu Cluj</span>
          <span className="logo-item">Policlinica Speranța Iași</span>
          <span className="logo-item">Centrul Medical Timișoara</span>
          <span className="logo-item">Clinica Floreasca</span>
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const { t } = useTranslation();
  return (
    <section className="section section-alt" id="features">
      <div className="container">
        <div className="section-label">{t('features.sectionLabel')}</div>
        <h2 className="section-title">
          {t('features.sectionTitleLine1')}<br /><em>{t('features.sectionTitleEm')}</em>
        </h2>
        <p className="section-body">{t('features.sectionBody')}</p>
        <div className="features-grid">
          {FEATURE_ITEMS.map((f) => (
            <div className="feature-card" key={f.key}>
              <div className="feature-icon">{f.icon}</div>
              <div className="feature-title">{t(`features.items.${f.key}.title`)}</div>
              <div className="feature-body">{t(`features.items.${f.key}.body`)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const { t } = useTranslation();
  return (
    <section className="section" id="how">
      <div className="container">
        <div className="section-label">{t('howItWorks.sectionLabel')}</div>
        <h2 className="section-title">
          {t('howItWorks.sectionTitleLine1')}<br /><em>{t('howItWorks.sectionTitleEm')}</em>
        </h2>
        <p className="section-body">{t('howItWorks.sectionBody')}</p>
        <div className="steps">
          {STEPS.map((step) => (
            <div className="step" key={step.key}>
              <div className="step-num">{step.num}</div>
              <div className="step-title">{t(`howItWorks.steps.${step.key}.title`)}</div>
              <div className="step-body">{t(`howItWorks.steps.${step.key}.body`)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const { t } = useTranslation();
  return (
    <section className="section section-alt">
      <div className="container">
        <div className="section-label">{t('testimonials.sectionLabel')}</div>
        <h2 className="section-title">
          {t('testimonials.sectionTitleLine1')}<br /><em>{t('testimonials.sectionTitleEm')}</em>
        </h2>
        <div className="testimonials-grid">
          {TESTIMONIALS.map((item) => (
            <div className="testimonial-card" key={item.key}>
              <div className="testimonial-stars">★★★★★</div>
              <div className="testimonial-stat">
                <span className="testimonial-stat-num">{t(`testimonials.items.${item.key}.statNum`)}</span>
                <span className="testimonial-stat-label">{t(`testimonials.items.${item.key}.statLabel`)}</span>
              </div>
              <p className="testimonial-quote">"{t(`testimonials.items.${item.key}.quote`)}"</p>
              <div className="testimonial-author">
                <div className="testimonial-av" style={{ background: item.bg }}>{item.av}</div>
                <div>
                  <div className="testimonial-name">{t(`testimonials.items.${item.key}.name`)}</div>
                  <div className="testimonial-role">{t(`testimonials.items.${item.key}.role`)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const { t, i18n } = useTranslation();
  const [seats, setSeats] = useState(3);
  const [annual, setAnnual] = useState(false);

  const effectivePrice = annual
    ? Math.round(PRICE_PER_SEAT * (1 - ANNUAL_DISCOUNT / 100))
    : PRICE_PER_SEAT;
  const total = effectivePrice * seats;
  const savedYearly = annual ? (PRICE_PER_SEAT - effectivePrice) * seats * 12 : 0;
  const numberLocale = i18n.language === 'ro' ? 'ro-RO' : 'en-US';

  return (
    <section className="section" id="pricing">
      <div className="container">
        <div className="section-label">{t('pricing.sectionLabel')}</div>
        <h2 className="section-title">
          {t('pricing.sectionTitleLine1')}<br /><em>{t('pricing.sectionTitleEm')}</em>
        </h2>
        <p className="section-body">{t('pricing.sectionBody')}</p>
        <div className="pricing-seat-wrap">
          <div className="pricing-calculator">
            <div className="pricing-seat-label">{t('pricing.calculator')}</div>

            <div className="billing-toggle">
              <button
                className={`billing-opt${!annual ? ' active' : ''}`}
                onClick={() => { setAnnual(false); }}
              >
                {t('pricing.monthly')}
              </button>
              <button
                className={`billing-opt${annual ? ' active' : ''}`}
                onClick={() => { setAnnual(true); }}
              >
                {t('pricing.annual')}{' '}
                <span className="billing-badge">
                  {t('pricing.annualDiscount', { discount: ANNUAL_DISCOUNT })}
                </span>
              </button>
            </div>

            <div className="seat-counter-row">
              <span className="seat-counter-label">{t('pricing.practitionersOnTeam')}</span>
              <div className="seat-counter-controls">
                <button
                  className="seat-btn"
                  aria-label={t('pricing.decrease')}
                  onClick={() => { setSeats((s) => Math.max(1, s - 1)); }}
                >
                  −
                </button>
                <span className="seat-num" data-testid="seat-count">{seats}</span>
                <button
                  className="seat-btn"
                  aria-label={t('pricing.increase')}
                  onClick={() => { setSeats((s) => s + 1); }}
                >
                  +
                </button>
              </div>
            </div>

            <div className="pricing-big-price">
              <span className="pricing-big-cur">{CURRENCY}</span>
              <span className="pricing-big-num">{total.toLocaleString(numberLocale)}</span>
              <span className="pricing-big-per">{t('pricing.perMonth')}</span>
            </div>
            <div className="pricing-breakdown">
              <strong>{seats} {t('pricing.practitioner', { count: seats })}</strong>
              {' × '}
              <strong>{effectivePrice} {CURRENCY}</strong>
              {' '}{t('pricing.perPractitionerPerMonth')}
              {annual && savedYearly > 0 && (
                <span style={{ color: '#1E5C47', marginLeft: 8, fontWeight: 600 }}>
                  {t('pricing.savings', {
                    amount: savedYearly.toLocaleString(numberLocale),
                    currency: CURRENCY,
                  })}
                </span>
              )}
            </div>

            <a
              href="/register"
              className="btn-primary"
              style={{ width: '100%', justifyContent: 'center', fontSize: 15, padding: '12px 0' }}
            >
              {t('pricing.ctaButton')}
            </a>
            <p className="pricing-trial-note">{t('pricing.trialNote')}</p>
          </div>

          <div className="pricing-features-col">
            <div className="pricing-features-title">{t('pricing.includedTitle')}</div>
            <ul className="pricing-features-list">
              {PRICING_FEATURE_KEYS.map((key) => (
                <li key={key}>{t(`pricing.features.${key}`)}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => {
    setOpenIndex((prev) => (prev === i ? null : i));
  };

  return (
    <section className="section section-alt" id="faq">
      <div className="container">
        <div className="section-label">{t('faq.sectionLabel')}</div>
        <h2 className="section-title">
          {t('faq.sectionTitleLine1')}<br /><em>{t('faq.sectionTitleEm')}</em>
        </h2>
        <div className="faq-list">
          {FAQ_KEYS.map((key, i) => (
            <div
              className={`faq-item${openIndex === i ? ' open' : ''}`}
              key={key}
              data-testid={`faq-item-${i}`}
            >
              <div className="faq-q" onClick={() => { toggle(i); }}>
                {t(`faq.items.${key}.question`)}
                <span className="faq-chevron">▼</span>
              </div>
              <div className="faq-a">
                <div className="faq-a-inner">{t(`faq.items.${key}.answer`)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  const { t } = useTranslation();
  return (
    <section className="cta-section">
      <div className="cta-inner">
        <h2 className="cta-title">
          {t('cta.titleLine1')}<br /><em>{t('cta.titleEm')}</em>
        </h2>
        <p className="cta-body">{t('cta.body')}</p>
        <a href="#pricing" className="btn-white">{t('cta.button')}</a>
        <p className="cta-note">{t('cta.note')}</p>
      </div>
    </section>
  );
}

function LandingFooter() {
  const { t } = useTranslation();
  return (
    <footer>
      <div className="footer-inner">
        <div className="footer-top">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 26, height: 26, background: '#1E5C47', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <LogoIcon size={14} />
              </div>
              <span className="footer-brand-name">calendo</span>
            </div>
            <p className="footer-brand-desc" style={{ marginTop: 10 }}>
              {t('footer.brandDesc')}
            </p>
          </div>
          <div className="footer-col">
            <div className="footer-col-title">{t('footer.product')}</div>
            <a href="#features">{t('footer.features')}</a>
            <a href="#pricing">{t('footer.pricing')}</a>
            <a href="/securitate">{t('footer.security')}</a>
            <a href="/blog">{t('footer.news')}</a>
          </div>
          <div className="footer-col">
            <div className="footer-col-title">{t('footer.company')}</div>
            <a href="/despre">{t('footer.about')}</a>
            <a href="/contact">{t('footer.contact')}</a>
          </div>
          <div className="footer-col">
            <div className="footer-col-title">{t('footer.resources')}</div>
            <a href="/docs">{t('footer.docs')}</a>
            <a href="/docs/start">{t('footer.gettingStarted')}</a>
            <a href="/suport">{t('footer.support')}</a>
            <Link to="/resources/status">{t('footer.status')}</Link>
          </div>
        </div>
        <div className="footer-bottom">
          <span className="footer-copy">{t('footer.copyright')}</span>
          <div className="footer-legal">
            <a href="/termeni">{t('footer.terms')}</a>
            <a href="/confidentialitate">{t('footer.privacy')}</a>
            <a href="/gdpr">{t('footer.gdpr')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── Page ─────────────────────────────────────────────────────
export function HomePage() {
  return (
    <div className="landing-root">
      <LandingNav />
      <main>
        <HeroSection />
        <LogosSection />
        <FeaturesSection />
        <HowItWorksSection />
        <TestimonialsSection />
        <PricingSection />
        <FaqSection />
        <CtaSection />
      </main>
      <LandingFooter />
    </div>
  );
}
